/**
 * 榜单1.0
 * @author: yangtuo@shizhuang-inc.com
 */
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { StickyContainer } from '@/common/sticky'
import { BaseComponentItemPropsType } from '@/typings/types'
import { Merge } from '@/typings/utils'
import useLangConfig from '@/hooks/business/useLangConfig'
import { Topbar, ProductListTab, TypeList, YouLike, MoreBooklist, CategoryList } from './components'
import {
  ListTypeEnum,
  ListType,
  ListItemConfig,
  ProductItemType,
  CategoryItemType,
  SelectItemProps,
} from './types'
import langConfigMap from './lang'
import Context, { TypeListContext, ProductContext, CategoryListContext } from './context'
import * as imgSrcMap from './constants'

import styles from './index.module.less'
import { getL1TabList, getBooklistByType } from '@/apis/share'
import { useWithReactFunctionalTrack } from '@du/analytics-sdk'
import { createTrackConfig } from './trackConfig'

type SubItemProps1 = {
  [K in keyof ListType]: ListItemConfig
}

type SubItemProps2 = {
  top1Icon: string
  top2Icon: string
  top3Icon: string
  otherIcon: string
}

type ItemProps = Merge<SubItemProps1, SubItemProps2>

interface Props extends BaseComponentItemPropsType<ItemProps> {}

function useTopbarProps({
  typeTabList,
  timeRange,
  listType,
}: {
  typeTabList: any
  timeRange: SelectItemProps[]
  categoryTab: number
  listType: number
}) {
  const cfg = typeTabList?.filter((it: any) => it.topType == listType)

  const config = useMemo(
    () => ({
      ...cfg?.[0],
      options: timeRange,
    }),
    [cfg, timeRange]
  )

  return config
}

function useTypeTabListProps(props: { typeTabList: any }) {
  const { typeTabList } = props
  const dataSource = useMemo(() => {
    return typeTabList.map((it: any) => ({ id: it.topId, key: it.topType, title: it.tabName }))
  }, [typeTabList])

  return {
    dataSource,
  }
}

const genKey = (listType: ListTypeEnum, filterValue: string) => `${listType}-${filterValue}`

/**
 * 榜单数据24小时更新一次，相同条件下 不重新请求数据
 * @param props
 */
function PoizonBooklist(props: Props) {
  const { editConfig, propsConfig } = props

  const { lang, currencyCode, langConfig } = useLangConfig({
    editConfig,
    langConfigMap,
  })

  const query = editConfig?.query
  // TODO useReducer
  const [categoryTab, setcategoryTab] = useState<number>(Number(query?.categoryId) || 29) // categoryId: 29 鞋类

  const [listType, setListType] = useState<number>(Number(query?.topType) || 0)
  const [listId, setListId] = useState<number>(Number(query?.topId) || 0)

  const [categoryTabList, setCategoryTabList] = useState<CategoryItemType[]>([])

  const [changeType, setChangeType] = useState(false)

  const [typeTabList, setTypeTabList] = useState([])
  const [timeRange, setTimeRange] = useState<SelectItemProps[]>([])
  const [dataList, setDataList] = useState<ProductItemType[]>([])
  const [filterValue, setFilterValue] = useState<string>('24h')
  const isQueryMapRef = useRef({})
  const scrollTopMapRef = useRef({})

  // 添加埋点
  const functionalPropsRef: React.MutableRefObject<{ $track?: any }> = useRef({})
  const functionalRef = useRef(null)

  useWithReactFunctionalTrack({
    functionalRef,
    functionalProps: functionalPropsRef.current,
    useEffect,
    createTrackConfig,
  })

  const trackHelper = (name, info = {}) => {
    functionalPropsRef.current.$track && functionalPropsRef.current.$track(name, info)
  }

  const topbarProps = useTopbarProps({
    typeTabList,
    timeRange,
    categoryTab,
    listType,
  })

  // 获取typeTabListProps
  const typeTabListProps = useTypeTabListProps({ typeTabList })

  const [moreTopList, setMoreTopList] = useState([])

  const setFilterValueByKey = useCallback((value: string) => {
    setFilterValue(value)
  }, [])

  const setCategoryTabListByList = useCallback(
    (cList: []) => {
      const categoryList = []
      cList?.map((it: any) => {
        categoryList.push({ key: it.categoryId, title: it.categoryName })

        if (categoryTab == it.categoryId && it.categoryTabList?.length > 0) {
          if (!changeType) {
            setTypeTabList(() => it.categoryTabList)
            setListType(() => it.categoryTabList?.[0].topType)
            setListId(() => it.categoryTabList?.[0].topId)
          }
          setChangeType(false)

          it.categoryTabList.map((tIt: any) => {
            if (listType == tIt.topType) {
              setTimeRange(tIt.categoryTimeRangeList)
              setFilterValue(tIt.categoryTimeRangeList?.[0].id)
            }
          })
        }
      })
      setCategoryTabList(categoryList)
    },
    [categoryTab, changeType, listType]
  )

  useEffect(() => {
    const key = genKey(listType, filterValue)

    if (isQueryMapRef.current[key]) {
      const oldScrollTop = scrollTopMapRef.current[listType]
      if (oldScrollTop || oldScrollTop === 0) {
        const dom = document.querySelector('html')
        dom.scrollTop = oldScrollTop
      }
    }

    getBooklistByType(
      {
        countryCode: editConfig.local,
        topId: listId,
        topType: listType,
        timeRang: filterValue,
        pageSize: 20,
        page: 1,
        language: editConfig.lang,
        categoryId: categoryTab,
      },
      {
        language: editConfig.lang,
      }
    ).then((res) => {
      if (res.data !== null) {
        const data = res.data
        isQueryMapRef.current[key] = true

        if (data.moreTopList && data.moreTopList.length < 6) {
          data.moreTopList = data.moreTopList.slice(0, 3)
        }

        setMoreTopList(data.moreTopList)
        setDataList(data.spuList)
      }
    })
  }, [categoryTab, editConfig.lang, editConfig.local, filterValue, listId, listType])

  useEffect(() => {
    getL1TabList({ countryCode: editConfig.local }, { language: editConfig.lang }).then((res) => {
      if (res.code == 200) {
        const categoryTopTabList = res?.data?.categoryTopTabList || []
        setCategoryTabListByList(categoryTopTabList)
      }
    })
  }, [editConfig.lang, editConfig.local, setCategoryTabListByList])

  const onCategoryChange = (value: number) => {
    if (categoryTab == value) return

    setcategoryTab((preValue) => {
      // 有副作用
      const dom = document.querySelector('html')
      scrollTopMapRef.current[preValue] = dom && dom.scrollTop
      return value
    })
  }

  const onTypeChange = (topType: number, topId: number) => {
    setListType(topType)
    setListId(topId)
    setChangeType(true)
  }

  let intersectionObserver = null

  const initObserver = () => {
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((item) => {
          if (item.intersectionRatio >= 0.5) {
            const params = {
              current_page: 515,
              block_type: item.target['dataset'].blockType,
              position: item.target['dataset'].index,
              spu_id: item.target['dataset'].spuId,
              poizon_list_id: listId,
              poizon_trade_tab_id: categoryTab,
              poizon_level_2_tab_id: listType,
            }
            trackHelper('poizon_trade_rank_list_product_exposure', params)
            intersectionObserver.unobserve(item.target)
          }
        })
      },
      {
        root: null,
        threshold: [0.5], // 阀值设为1，当只有比例达到0.5时才触发回调函数
      }
    )
  }

  const setObserver = (dataList) => {
    if (!intersectionObserver) initObserver()
    const productElements = [...document.querySelectorAll('[data-spu-id]')]
    if (productElements.length) {
      const eles = productElements.slice(dataList.length)
      eles.forEach((item) => intersectionObserver.observe(item))
    }
  }

  useEffect(() => {
    setObserver([])
  }, [dataList])

  const handleTrack = (position, spu_id, block_type) => {
    const params = {
      current_page: 515,
      block_type,
      position,
      spu_id,
      poizon_list_id: listId,
      poizon_trade_tab_id: categoryTab,
      poizon_level_2_tab_id: listType,
    }
    trackHelper('poizon_trade_rank_list_product_click', params)
  }

  return (
    <div ref={functionalRef} className={styles['poizon-book-list']}>
      <Context.Provider
        value={{
          lang,
          currencyCode,
          langConfig,
          editConfig: props.editConfig as any,
          trackHelper,
          handleTrack,
        }}
      >
        <Topbar
          {...topbarProps}
          lang={lang}
          filterValue={filterValue}
          onFilterChange={setFilterValueByKey}
        />

        <StickyContainer>
          <CategoryListContext.Provider value={{ onCategoryChange, lang }}>
            <CategoryList dataSource={categoryTabList} activeKey={categoryTab} />
          </CategoryListContext.Provider>

          <TypeListContext.Provider value={{ onTypeChange, lang }}>
            <TypeList {...typeTabListProps} activeKey={listType} />
          </TypeListContext.Provider>

          <ProductContext.Provider
            value={{
              top1Icon: propsConfig.top1Icon || imgSrcMap.top1Icon,
              top2Icon: propsConfig.top2Icon || imgSrcMap.top2Icon,
              top3Icon: propsConfig.top3Icon || imgSrcMap.top3Icon,
              otherIcon: propsConfig.otherIcon || imgSrcMap.otherIcon,
              currencyCode,
              local: editConfig.local,
              langConfig,
            }}
          >
            <ProductListTab listType={listType} dataSourceList={dataList} />
          </ProductContext.Provider>

          {moreTopList?.length >= 3 && (
            <MoreBooklist
              moreTopList={moreTopList}
              langConfig={langConfig}
              lang={lang}
              currencyCode={currencyCode}
            />
          )}
          <YouLike
            topId={listId}
            categoryId={categoryTab}
            timeRange={filterValue}
            onObserver={(data) => setObserver([...dataList, ...data])}
          />
        </StickyContainer>
      </Context.Provider>
    </div>
  )
}

PoizonBooklist.config = {
  name: 'poizon-booklist',
}

export default PoizonBooklist
function e(e: any) {
  throw new Error('Function not implemented.')
}
